import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Divider,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  Alert,
} from '@mui/material';
import {
  Edit,
  Business,
  Email,
  Person,
  Badge,
  CalendarToday,
  Save,
  Cancel,
  CloudUpload,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { companyApi } from '../services/api/companyApi';
import { userApi } from '../services/api/userApi';
import { AccountType } from '../types/auth';
import PageHeader from '../components/common/PageHeader';

const ProfilePage: React.FC = () => {
  const theme = useTheme();
  const { user, token, setAuth } = useAuthStore();
  const [company, setCompany] = useState<any>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    branch: user?.branch || '',
    mail: user?.mail || '',
  });

  // Fetch company data
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (user?.companyId) {
        try {
          const response = await companyApi.getById(user.companyId);
          setCompany(response);
        } catch (error) {
          console.error('Failed to fetch company data:', error);
        }
      }
    };

    fetchCompanyData();
  }, [user?.companyId]);

  const getAccountTypeLabel = (accountType: AccountType) => {
    switch (accountType) {
      case AccountType.MasterAdmin:
        return 'Master Admin';
      case AccountType.CarbonCareAdmin:
        return 'CarbonCare Admin';
      case AccountType.LicenseeAdmin:
        return 'Licensee Admin';
      case AccountType.User:
        return 'User';
      case AccountType.SubCompanyAdmin:
        return 'Sub Company Admin';
      case AccountType.BranchAdmin:
        return 'Branch Admin';
      case AccountType.SubCustomerAdmin:
        return 'Sub Customer Admin';
      default:
        return 'Unknown';
    }
  };

  const getAccountTypeColor = (accountType: AccountType) => {
    switch (accountType) {
      case AccountType.MasterAdmin:
        return 'error';
      case AccountType.CarbonCareAdmin:
        return 'warning';
      case AccountType.LicenseeAdmin:
        return 'primary';
      case AccountType.User:
        return 'secondary';
      default:
        return 'default';
    }
  };

  const handleEditClick = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      branch: user?.branch || '',
      mail: user?.mail || '',
    });
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setError(null);
    setSuccess(null);
    setProfileImageFile(null);
    setValidationErrors({});
  };

  const handleProfileImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        setValidationErrors(prev => ({
          ...prev,
          profileImage: 'Please select a valid image file (JPG, PNG, GIF, SVG)'
        }));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors(prev => ({
          ...prev,
          profileImage: 'File size must be less than 5MB'
        }));
        return;
      }

      setProfileImageFile(file);
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.profileImage;
        return newErrors;
      });
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Handle profile image file upload if present
      let updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        branch: formData.branch,
        mail: formData.mail,
      };

      if (profileImageFile) {
        // Convert file to base64 data URL
        const profileImageDataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve(e.target?.result as string);
          };
          reader.readAsDataURL(profileImageFile);
        });
        updateData.profileImageUrl = profileImageDataUrl;
      }

      const updatedUser = await userApi.updateProfile(user.id, updateData);

      // Update auth store with new user data
      if (token) {
        setAuth(updatedUser, token);
      }

      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        handleCloseEdit();
        globalThis.location.reload(); // Reload to ensure all components update
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!user) {
    return (
      <Box>
        <Typography variant="h4">Profile</Typography>
        <Typography color="error">User data not available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader description="View and update your personal information." />

      <Grid container spacing={3}>
        {/* User Information Card */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ '&:last-child': { pb: 4 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person /> Personal Information
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={handleEditClick}
                  sx={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main }}
                >
                  Edit Profile
                </Button>
              </Box>

              {/* Profile Header Row: avatar + name/email/role */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                {(() => {
                  if (user.profileImageUrl) {
                    return (
                  <Avatar
                    src={user.profileImageUrl}
                    alt={`${user.firstName} ${user.lastName}`}
                    sx={{ width: 80, height: 80, flexShrink: 0 }}
                  />
                    );
                  }
                  if (company?.logoUrl || company?.logo_url) {
                    return (
                  <Avatar
                    src={company.logoUrl || company.logo_url}
                    alt={company.name || 'Company logo'}
                    sx={{ width: 80, height: 80, flexShrink: 0 }}
                  />
                    );
                  }
                  return (
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      flexShrink: 0,
                      backgroundColor: theme.palette.primary.main,
                      fontSize: '2rem',
                    }}
                  >
                    {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                  </Avatar>
                  );
                })()}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {user.email}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={getAccountTypeLabel(user.accountType)}
                      color={getAccountTypeColor(user.accountType) as any}
                      icon={<Badge />}
                      size="small"
                    />
                    <Chip
                      label={user.isActive ? 'Active' : 'Inactive'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      First Name
                    </Typography>
                    <Typography variant="body1">{user.firstName}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Last Name
                    </Typography>
                    <Typography variant="body1">{user.lastName}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Email Address
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" />
                      {user.email}
                    </Typography>
                  </Box>
                </Grid>
                {user.mail && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Business Email
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" />
                        {user.mail}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {user.branch && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Position/Title
                      </Typography>
                      <Typography variant="body1">{user.branch}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Member Since
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" />
                      {formatDate(user.createdAt || (user as any).created_at)}
                    </Typography>
                  </Box>
                </Grid>
                {user.lastLogin && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Login
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday fontSize="small" />
                        {formatDate(user.lastLogin || (user as any).last_login)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Company Information Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Business /> Company Information
              </Typography>

              {company ? (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  {company.logoUrl ? (
                    <Avatar
                      src={company.logoUrl}
                      alt={company.name}
                      sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        backgroundColor: theme.palette.secondary.main,
                        fontSize: '2rem'
                      }}
                    >
                      {company.name?.charAt(0) || 'C'}
                    </Avatar>
                  )}
                  <Typography variant="h6" gutterBottom>
                    {company.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {company.customerType || (company).customer_type || 'Licensee'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading company information...
                  </Typography>
                </Box>
              )}

              {company && (
                <Divider sx={{ my: 2 }} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Profile Image Upload */}
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box
                  component="label"
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    width: 130,
                    height: 130,
                    mx: 'auto',
                    mb: 2,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: validationErrors.profileImage
                      ? `2px solid ${theme.palette.error.main}`
                      : `2px solid ${theme.palette.primary.main}33`,
                    transition: 'border-color 0.25s ease, transform 0.25s ease',
                    '&:hover': {
                      borderColor: validationErrors.profileImage
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                    },
                    '&:hover .avatar-overlay': {
                      opacity: 1,
                    },
                  }}
                >
                  {(() => {
                    if (profileImageFile) {
                      return (
                    <Avatar
                      src={URL.createObjectURL(profileImageFile)}
                      alt="Profile preview"
                      sx={{ width: '100%', height: '100%' }}
                    />
                      );
                    }
                    if (user.profileImageUrl) {
                      return (
                    <Avatar
                      src={user.profileImageUrl}
                      alt={`${user.firstName} ${user.lastName}`}
                      sx={{ width: '100%', height: '100%' }}
                    />
                      );
                    }
                    if (company?.logoUrl || company?.logo_url) {
                      return (
                    <Avatar
                      src={company.logoUrl || company.logo_url}
                      alt={company.name || 'Company logo'}
                      sx={{ width: '100%', height: '100%' }}
                    />
                      );
                    }
                    return (
                    <Avatar
                      sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: theme.palette.primary.main,
                        fontSize: '3rem',
                      }}
                    >
                      {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                    </Avatar>
                    );
                  })()}

                  {/* Hover overlay */}
                  <Box
                    className="avatar-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.55)',
                      color: '#fff',
                      opacity: 0,
                      transition: 'opacity 0.25s ease',
                      pointerEvents: 'none',
                    }}
                  >
                    <CloudUpload fontSize="medium" />
                    <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
                      Update Photo
                    </Typography>
                  </Box>

                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    disabled={loading}
                  />
                </Box>

                {validationErrors.profileImage && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    {validationErrors.profileImage}
                  </Typography>
                )}
                {profileImageFile && (
                  <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                    Image selected: {profileImageFile.name}
                  </Typography>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={loading || user.accountType === AccountType.MasterAdmin}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pt: 0, pb: 2 }}>
          <Button onClick={handleCloseEdit} disabled={loading} startIcon={<Cancel />} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveProfile}
            disabled={loading}
            variant="contained"
            startIcon={<Save />}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;